import { createBrowserRouter } from "react-router";

import {
  GUEST_ONLY_ROUTES,
  PROTECTED_ROUTES,
  PUBLIC_ROUTES,
  ADMIN_ROUTES,
} from "@/constants/routes";
import { NotificationsPage } from "@/features/engagement/pages/NotificationsPage";
import { WishlistPage } from "@/features/engagement/pages/WishlistPage";
import { ProfilePage } from "@/features/profile/pages/ProfilePage";
import { PublicProfilePage } from "@/features/profile/pages/PublicProfilePage";
import { SettingsPage } from "@/features/profile/pages/SettingsPage";
import { LoginPage } from "@/features/identity/pages/LoginPage";
import { OtpVerificationPage } from "@/features/identity/pages/OtpVerificationPage";
import { RegisterPage } from "@/features/identity/pages/RegisterPage";
import { MyListingsPage } from "@/features/listings/pages/MyListingsPage";
import { ChatConversationPage } from "@/features/messaging/pages/ChatConversationPage";
import { ChatEmptyDetailPage } from "@/features/messaging/pages/ChatEmptyDetailPage";
import { ChatPage } from "@/features/messaging/pages/ChatPage";
import { SellDetailsPage } from "@/features/listings/pages/SellDetailsPage";
import { SellFinalizePage } from "@/features/listings/pages/SellFinalizePage";
import { SellMediaPage } from "@/features/listings/pages/SellMediaPage";
import { HomePage } from "@/features/marketplace/pages/HomePage";
import { ProductDetailsPage } from "@/features/marketplace/pages/ProductDetailsPage";
import { SearchResultsPage } from "@/features/marketplace/pages/SearchResultsPage";
import { AdminLayout } from "@/layouts/AdminLayout";
import { AuthenticatedLayout } from "@/layouts/AuthenticatedLayout";
import { PublicLayout } from "@/layouts/PublicLayout";
import { WizardLayout } from "@/layouts/WizardLayout";
import { NotFoundPage } from "@/pages/NotFoundPage";
import { PlaceholderPage } from "@/pages/PlaceholderPage";
import { RequireAuth } from "@/routes/guards/RequireAuth";
import { RequireGuest } from "@/routes/guards/RequireGuest";

/**
 * The full route tree from docs/ui/routes.md, nested under the four layouts
 * docs/ui/layouts.md defines. The identity feature (Login/Register/OTP
 * Verification) is real; every other leaf still renders the generic
 * `PlaceholderPage` (titled per route) until its feature is built — each
 * becomes a `lazy: () => import("../pages/XPage")` import at that point,
 * per `docs/architecture/frontend-architecture.md`'s "lazy loaded routes"
 * principle.
 *
 * One placement is a simplification, not a doc-accurate mapping, flagged
 * here rather than silently treated as settled:
 *
 * - Guest-only routes are nested under `PublicLayout`. The correct
 *   long-term home is a dedicated `GuestAuthLayout` (docs/ui/layouts.md) —
 *   out of scope for the four layouts actually requested so far.
 *
 * `/sell/*` now uses the doc-accurate `WizardLayout`; `/listings/:id/edit`
 * stays a placeholder under it since no edit flow is wireframed anywhere in
 * the export (`docs/ui/routes.md`'s own note).
 *
 * Guards now wired per docs/ui/routes.md's redirect rules: `RequireGuest`
 * wraps Login/Register/OTP, `RequireAuth` wraps every Protected AND Admin
 * route (an admin route only needs *authentication* to redirect correctly
 * today — role-checking (`RequireRole`) waits on Admin/Moderator existing
 * in the auth model, per RequireAuth.tsx's own note).
 */

function page(title: string, description: string) {
  return <PlaceholderPage title={title} description={description} />;
}

export const router = createBrowserRouter([
  {
    element: <PublicLayout />,
    children: [
      { path: PUBLIC_ROUTES.home, element: <HomePage /> },
      { path: PUBLIC_ROUTES.search, element: <SearchResultsPage /> },
      { path: PUBLIC_ROUTES.productDetails, element: <ProductDetailsPage /> },
      { path: PUBLIC_ROUTES.sellerProfile, element: <PublicProfilePage /> },
      { path: PUBLIC_ROUTES.helpCenter, element: page("Help Center", "Not built yet.") },
      { path: PUBLIC_ROUTES.helpArticle, element: page("FAQ Article", "Not built yet.") },

      {
        element: <RequireGuest />,
        children: [
          { path: GUEST_ONLY_ROUTES.login, element: <LoginPage /> },
          { path: GUEST_ONLY_ROUTES.register, element: <RegisterPage /> },
          { path: GUEST_ONLY_ROUTES.otpVerification, element: <OtpVerificationPage /> },
        ],
      },
      {
        path: GUEST_ONLY_ROUTES.forgotPassword,
        element: page("Forgot password", "Not wireframed yet."),
      },
      {
        path: GUEST_ONLY_ROUTES.resetPassword,
        element: page("Reset password", "Not wireframed yet."),
      },

      { path: "*", element: <NotFoundPage /> },
    ],
  },
  {
    element: <AuthenticatedLayout />,
    children: [
      {
        element: <RequireAuth />,
        children: [
          { path: PROTECTED_ROUTES.myListings, element: <MyListingsPage /> },
          { path: PROTECTED_ROUTES.wishlist, element: <WishlistPage /> },
          {
            path: PROTECTED_ROUTES.chat,
            element: <ChatPage />,
            children: [
              { index: true, element: <ChatEmptyDetailPage /> },
              { path: ":conversationId", element: <ChatConversationPage /> },
            ],
          },
          { path: PROTECTED_ROUTES.notifications, element: <NotificationsPage /> },
          { path: PROTECTED_ROUTES.profile, element: <ProfilePage /> },
          { path: PROTECTED_ROUTES.settings, element: <SettingsPage /> },
        ],
      },
    ],
  },
  {
    element: <WizardLayout />,
    children: [
      {
        element: <RequireAuth />,
        children: [
          { path: PROTECTED_ROUTES.sellMedia, element: <SellMediaPage /> },
          { path: PROTECTED_ROUTES.sellDetails, element: <SellDetailsPage /> },
          { path: PROTECTED_ROUTES.sellFinalize, element: <SellFinalizePage /> },
          {
            path: PROTECTED_ROUTES.editListing,
            element: page("Edit Listing", "Not wireframed yet."),
          },
        ],
      },
    ],
  },
  {
    element: <AdminLayout />,
    children: [
      {
        element: <RequireAuth />,
        children: [
          { path: ADMIN_ROUTES.dashboard, element: page("Admin Overview", "Not built yet.") },
          {
            path: ADMIN_ROUTES.users,
            element: page("Admin — User Management", "Not built yet."),
          },
          {
            path: ADMIN_ROUTES.listings,
            element: page("Admin — Listing Moderation", "Not built yet."),
          },
          {
            path: ADMIN_ROUTES.verifications,
            element: page("Admin — Verification Review", "Not built yet."),
          },
          {
            path: ADMIN_ROUTES.settings,
            element: page("Admin — Platform Settings", "Not built yet."),
          },
          { path: ADMIN_ROUTES.reports, element: page("Admin — Reports", "Not wireframed yet.") },
        ],
      },
    ],
  },
]);
